import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

const buildConversationInsertPayload = (messageData, useCamelCase = false) => {
  const userId = messageData.userId ?? messageData.user_id;
  const twinId = messageData.twinId ?? messageData.twin_id;
  const messageType = messageData.messageType ?? messageData.message_type ?? messageData.sender;
  const metadata = messageData.metadata ?? {};

  return useCamelCase
    ? {
        userId,
        twinId,
        // messageType,
        content: messageData.content,
        metadata,
        
      }
    : {
        user_id: userId,
        twin_id: twinId,
        // message_type: messageType,
        content: messageData.content,
        metadata,
      };
};

const buildConversationQuery = (userId, twinId, limit = 50, useCamelCase = false) => {
  const query = supabase.from("conversations").select("*").limit(limit);
  query.eq(useCamelCase ? "userId" : "user_id", userId);
  if (twinId) {
    query.eq(useCamelCase ? "twinId" : "twin_id", twinId);
  }
  return query;
};

// Database helper functions
export const db = {
  // Profile operations
  async getProfile(userId) {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) throw error;
    return data;
  },

  async updateProfile(userId, updates) {
    const { data, error } = await supabase
      .from("profiles")
      .upsert({
        id: userId,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // AI Twin operations
  async getAITwins(userId) {
    const { data, error } = await supabase
      .from("ai_twins")
      .select("*")
      .eq("user_id", userId)
      .eq("is_active", true);

    if (error) throw error;
    return data;
  },

  async createAITwin(twinData) {
    const { data, error } = await supabase
      .from("ai_twins")
      .insert(twinData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateAITwin(twinId, updates) {
    const { data, error } = await supabase
      .from("ai_twins")
      .update(updates)
      .eq("id", twinId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Conversation operations
  async saveMessage(messageData) {
    const payload = {
      user_id: messageData.user_id || messageData.userId,
      twin_id: messageData.twin_id || messageData.twinId,
      content: messageData.content,
      message_type: messageData.sender || messageData.messageType || messageData.message_type,
      metadata: messageData.metadata || {},
      voice_message_id: messageData.voice_message_id || messageData.voiceMessageId,
      created_at: messageData.created_at || new Date().toISOString()
    };

    const { data, error } = await supabase
      .from("conversations")
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error("Error saving message:", error);
      throw error;
    }
    return data;
  },

  async getConversations(userId, twinId, limit = 50) {
    const { data, error } = await buildConversationQuery(
      userId,
      twinId,
      limit,
      false
    ).order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching conversations:", error);
      throw error;
    }

    // Map message_type back to sender for frontend compatibility
    return data.map(msg => ({
      ...msg,
      sender: msg.message_type,
      timestamp: msg.created_at
    })).reverse(); // Return in chronological order
  },

  // Mood tracking operations
  async saveMoodEntry(moodData) {
    const { data, error } = await supabase
      .from("mood_entries")
      .insert(moodData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getMoodEntries(userId, limit = 30) {
    const { data, error } = await supabase
      .from("mood_entries")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  // Reminder operations
  async saveReminder(reminderData) {
    const { data, error } = await supabase
      .from("reminders")
      .insert(reminderData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getReminders(userId) {
    const { data, error } = await supabase
      .from("reminders")
      .select("*")
      .eq("user_id", userId)
      .eq("is_active", true)
      .order("scheduled_for", { ascending: true });

    if (error) throw error;
    return data;
  },

  async updateReminder(reminderId, updates) {
    const { data, error } = await supabase
      .from("reminders")
      .update(updates)
      .eq("id", reminderId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteReminder(reminderId) {
    const { error } = await supabase
      .from("reminders")
      .delete()
      .eq("id", reminderId);

    if (error) throw error;
  },

  // Memory operations
  async saveMemory(memoryData) {
    const { data, error } = await supabase
      .from("memories")
      .insert(memoryData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getMemories(userId, twinId, limit = 100) {
    const { data, error } = await supabase
      .from("memories")
      .select("*")
      .eq("user_id", userId)
      .eq("twin_id", twinId)
      .order("importance_score", { ascending: false })
      .order("last_accessed", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  async deleteMemory(memoryId) {
    const { error } = await supabase
      .from("memories")
      .delete()
      .eq("id", memoryId);

    if (error) throw error;
  },

  async createVisualAnalysis(analysisData) {
    const { data, error } = await supabase
      .from("visual_analyses")
      .insert(analysisData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async saveRecommendation(recommendationData) {
    const { data, error } = await supabase
      .from("ai_recommendations")
      .insert(recommendationData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getRecommendations(userId, limit = 30) {
    const { data, error } = await supabase
      .from("ai_recommendations")
      .select("*, product_catalog(*)")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  async saveBiometricSnapshot(snapshotData) {
    const { data, error } = await supabase
      .from("biometric_snapshots")
      .insert(snapshotData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getLatestBiometricSnapshot(userId) {
    const { data, error } = await supabase
      .from("biometric_snapshots")
      .select("*")
      .eq("user_id", userId)
      .order("recorded_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getCreditWallet(userId) {
    const { data, error } = await supabase
      .from("credit_wallets")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async spendCredits(userId, amount, featureType, metadata = {}) {
    const { data, error } = await supabase.rpc("spend_credits", {
      p_user_id: userId,
      p_amount: amount,
      p_feature_type: featureType,
      p_metadata: metadata,
    });

    if (error) throw error;
    return data;
  },

  async getWellnessExerciseCompletions(userId, weekNumber) {
    const { data, error } = await supabase
      .from("wellness_exercise_completions")
      .select("*")
      .eq("user_id", userId)
      .eq("week_number", weekNumber)
      .order("completed_at", { ascending: false });

    if (error) throw error;
    return data;
  },

  async saveWellnessExerciseCompletion(completionData) {
    const { data, error } = await supabase
      .from("wellness_exercise_completions")
      .upsert(
        {
          ...completionData,
          completed_at: completionData.completed_at || new Date().toISOString(),
        },
        { onConflict: "user_id,week_number,workout_id,exercise_id" }
      )
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteWellnessExerciseCompletion(userId, weekNumber, workoutId, exerciseId) {
    const { error } = await supabase
      .from("wellness_exercise_completions")
      .delete()
      .eq("user_id", userId)
      .eq("week_number", weekNumber)
      .eq("workout_id", workoutId)
      .eq("exercise_id", exerciseId);

    if (error) throw error;
  },

  async getWellnessMealCompletions(userId, weekNumber) {
    const { data, error } = await supabase
      .from("wellness_meal_completions")
      .select("*")
      .eq("user_id", userId)
      .eq("week_number", weekNumber)
      .order("completed_at", { ascending: false });

    if (error) throw error;
    return data;
  },

  async saveWellnessMealCompletion(completionData) {
    const { data, error } = await supabase
      .from("wellness_meal_completions")
      .upsert(
        {
          ...completionData,
          completed_at: completionData.completed_at || new Date().toISOString(),
        },
        { onConflict: "user_id,week_number,meal_id" }
      )
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteWellnessMealCompletion(userId, weekNumber, mealId) {
    const { error } = await supabase
      .from("wellness_meal_completions")
      .delete()
      .eq("user_id", userId)
      .eq("week_number", weekNumber)
      .eq("meal_id", mealId);

    if (error) throw error;
  },

  async getWellnessCheckins(userId, limit = 28) {
    const { data, error } = await supabase
      .from("wellness_checkins")
      .select("*")
      .eq("user_id", userId)
      .order("checkin_date", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data;
  },

  async saveWellnessCheckin(checkin) {
    const { data, error } = await supabase
      .from("wellness_checkins")
      .upsert(checkin, { onConflict: "user_id,checkin_date" })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Breaking Silence operations
  async createPost(postData) {
    const { data, error } = await supabase
      .from("breaking_silence_posts")
      .insert(postData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updatePost(postId, updates) {
    const { data, error } = await supabase
      .from("breaking_silence_posts")
      .update(updates)
      .eq("id", postId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getPosts(limit = 20, offset = 0, includeReplies = false) {
    // First get posts
    let query = supabase
      .from("breaking_silence_posts")
      .select(
        `
      *,
      profiles:user_id (id, name, email)
    `,
        includeReplies ? undefined : { count: "exact" }
      )
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    const { data: posts, error, count } = await query;

    if (error) throw error;
    if (!posts || posts.length === 0) {
      return includeReplies
        ? { posts: [], pagination: {} }
        : { posts: [], pagination: { hasMore: false } };
    }

    // If replies are needed, fetch them in a separate optimized query
    if (includeReplies) {
      const postIds = posts.map((post) => post.id);

      const { data: replies, error: repliesError } = await supabase
        .from("breaking_silence_replies")
        .select(
          `
        *,
        profiles:user_id (id, name, email)
      `
        )
        .in("post_id", postIds)
        .order("created_at", { ascending: true });

      if (repliesError) throw repliesError;

      // Group replies by post_id for efficient merging
      const repliesByPostId = {};
      replies?.forEach((reply) => {
        if (!repliesByPostId[reply.post_id]) {
          repliesByPostId[reply.post_id] = [];
        }
        repliesByPostId[reply.post_id].push(reply);
      });

      // Merge replies with posts
      const postsWithReplies = posts.map((post) => ({
        ...post,
        breaking_silence_replies: repliesByPostId[post.id] || [],
      }));

      return {
        posts: postsWithReplies,
        pagination: {
          currentPage: Math.floor(offset / limit) + 1,
          totalPages: Math.ceil((count || 0) / limit),
          hasMore: offset + limit < (count || 0),
          totalCount: count || 0,
        },
      };
    }

    return {
      posts: posts,
      pagination: {
        currentPage: Math.floor(offset / limit) + 1,
        totalPages: Math.ceil((count || 0) / limit),
        hasMore: offset + limit < (count || 0),
        totalCount: count || 0,
      },
    };
  },

  async updatePostHearts(postId, postData = null) {
    try {
      let data = null;
      let error = null;

      // Update post if postData is provided
      if (postData) {
        const res = await supabase
          .from("breaking_silence_posts")
          .update(JSON.stringify(postData))
          .eq("id", postId)
          .select(); // important!

        data = res.data?.[0] ?? null; // take first row
        error = res.error;
        if (error) throw error;
      }

      // Call the RPC to increment hearts
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "increment_hearts_and_return",
        {
          post_id_val: postId,
          increment_by: 1,
        }
      );

      if (rpcError) throw rpcError;

      // Return the updated record (prefer rpc result if available)
      return rpcData ?? data;
    } catch (err) {
      console.error("Error updating post hearts:", err.message);
      throw err;
    }
  },
  async createReply(replyData) {
    const { data, error } = await supabase
      .from("breaking_silence_replies")
      .insert(replyData)
      .select()
      .single();

    if (error) throw error;

    // await supabase
    //   .from("breaking_silence_posts")
    //   .update({ replies_count: supabase.increment(1) })
    //   .eq("id", replyData.post_id);
    await supabase.rpc("increment_replies_count", {
      post_id_val: replyData.post_id,
    });
    return data;
  },

  // Subscription operations
  async getSubscription(userId) {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .eq("status", "active")
      .single();

    if (error && error.code !== "PGRST116") throw error;
    return data;
  },

  // Usage tracking
  async trackUsage(userId, featureType, count = 1) {
    const { error } = await supabase.rpc("track_feature_usage", {
      p_user_id: userId,
      p_feature_type: featureType,
      p_count: count,
    });

    if (error) throw error;
  },

  async checkFeatureLimit(userId, featureType, limit) {
    const { data, error } = await supabase.rpc("check_feature_limit", {
      p_user_id: userId,
      p_feature_type: featureType,
      p_limit: limit,
    });

    if (error) throw error;
    return data;
  },

  async getUsageStats(userId) {
    const today = new Date().toISOString().split("T")[0];
    const { data, error } = await supabase
      .from("usage_tracking")
      .select("feature_type, usage_count")
      .eq("user_id", userId)
      .eq("date", today);

    if (error) throw error;

    // Convert to object for easier access
    const stats = {};
    data.forEach((item) => {
      stats[item.feature_type] = item.usage_count;
    });

    return stats;
  },
};

// Real-time subscriptions
export const subscribeToConversations = (userId, twinId, callback) => {
  const channel = supabase.channel("conversations");

  // Support both snake_case and camelCase conversation column names.
  [
    `user_id=eq.${userId} AND twin_id=eq.${twinId}`,
    `userId=eq.${userId} AND twinId=eq.${twinId}`,
  ].forEach((filter) => {
    channel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "conversations",
        filter,
      },
      callback
    );
  });

  return channel.subscribe();
};

export const subscribeToMoodEntries = (userId, callback) => {
  return supabase
    .channel("mood_entries")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "mood_entries",
        filter: `user_id=eq.${userId}`,
      },
      callback
    )
    .subscribe();
};

export const subscribeToReminders = (userId, callback) => {
  return supabase
    .channel("reminders")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "reminders",
        filter: `user_id=eq.${userId}`,
      },
      callback
    )
    .subscribe();
};

export const subscribeToBreakingSilence = (callback) => {
  return supabase
    .channel("breaking_silence_posts")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "breaking_silence_posts",
        filter: "is_active=eq.true",
      },
      callback
    )
    .subscribe();
};

export const subscribeToPostReplies = (postId, callback) => {
  const subscription = supabase.channel(
    postId && postId !== "*"
      ? `breaking_silence_replies:${postId}`
      : "breaking_silence_replies"
  );

  const filter =
    postId && postId !== "*" ? `post_id=eq.${postId}` : undefined;

  return subscription
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "breaking_silence_replies",
        ...(filter ? { filter } : {}),
      },
      callback
    )
    .subscribe();
};
